import React, { useState, useRef, useEffect } from 'react';
import { generateHealthResponse } from '../../../app/data-access/geminiService.ts';
import { ChatMessage, UserProfile } from '../../../app/domain/types/index.ts';
import { MessageSquare, X, Send, Sparkles, Droplets, Utensils, MapPin, CheckCircle, ArrowRight } from 'lucide-react';

interface ChatWidgetProps {
  user: UserProfile;
}

interface WidgetAction {
  type: 'logWater' | 'findFacility' | 'swapMeal';
  data: any;
  completed?: boolean;
}

interface EnhancedChatMessage extends ChatMessage {
  widget?: WidgetAction;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<EnhancedChatMessage[]>([
    { role: 'model', text: `Hi ${user.name.split(' ')[0]}. I'm your Wellness AI. I know you're focusing on ${user.goal}. How can I help today?`, timestamp: Date.now() }
  ]);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, loading]);

  const handleSend = async (textOverride?: string) => {
    const textToSend = textOverride || input;
    if (!textToSend.trim() || loading) return;

    const userMsg: EnhancedChatMessage = { role: 'user', text: textToSend, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const context = `User Name: ${user.name}, Age: ${user.age}, Ethnicity: ${user.ethnicity}, BMI: approx ${(user.weightKg / Math.pow(user.heightCm/100, 2)).toFixed(1)}, Goal: ${user.goal}, Location: ${user.location}`;

    try {
        const response = await generateHealthResponse(textToSend, context);

        let botText = response?.text || "I'm processing that request...";
        let widget: WidgetAction | undefined = undefined;

        // Check for function calls
        const functionCalls = response?.functionCalls;
        if (functionCalls && functionCalls.length > 0) {
            const call = functionCalls[0];
            const args = call.args as any;

            if (call.name === 'logWater') {
                botText = `Logging ${args.amount}ml of water for you.`;
                widget = { type: 'logWater', data: args };
            } else if (call.name === 'findFacility') {
                botText = `Here are some ${args.category}s near ${user.location || 'you'}.`;
                widget = { type: 'findFacility', data: args };
            } else if (call.name === 'swapMeal') {
                botText = `I found a great ${args.preference || ''} alternative for ${args.mealType}.`;
                widget = { type: 'swapMeal', data: args };
            }
        }

        const botMsg: EnhancedChatMessage = { role: 'model', text: botText, timestamp: Date.now(), widget };
        setMessages(prev => [...prev, botMsg]);
    } catch (e) {
        setMessages(prev => [...prev, { role: 'model', text: "Sorry, I encountered an error. Please try again.", timestamp: Date.now() }]);
    } finally {
        setLoading(false);
    }
  };

  const quickActions = [
    { label: "Swap Lunch", icon: Utensils, query: "I want to swap my lunch for something lighter." },
    { label: "Log Water", icon: Droplets, query: "Log 500ml of water." },
    { label: "Find Gym", icon: MapPin, query: "Find a gym nearby." },
  ];

  // Render Widget Content
  const renderWidget = (widget: WidgetAction) => {
    if (widget.type === 'logWater') {
        return (
            <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800 flex items-center gap-3">
                <div className="bg-blue-100 dark:bg-blue-800 p-2 rounded-full text-blue-600 dark:text-blue-300">
                    <Droplets size={18} />
                </div>
                <div>
                    <p className="text-sm font-bold text-gray-800 dark:text-white">{widget.data.amount}ml Added</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Daily Goal: 2000ml</p>
                </div>
                <CheckCircle size={18} className="ml-auto text-green-500" />
            </div>
        );
    }
    if (widget.type === 'findFacility') {
        return (
            <div className="mt-2 p-3 bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm">
                <div className="flex items-center gap-2 mb-2 text-sm font-bold text-gray-800 dark:text-white">
                    <MapPin size={16} className="text-red-500" />
                    <span>Nearby {widget.data.category}s</span>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 hover:bg-stone-50 dark:hover:bg-stone-700/50 rounded-lg cursor-pointer transition-colors">
                        <div>
                            <p className="text-xs font-bold text-gray-900 dark:text-white">{widget.data.category === 'gym' ? 'FitLife Gym' : 'Green Leaf Cafe'}</p>
                            <p className="text-[10px] text-gray-500">0.8 mi • 4.8 ★</p>
                        </div>
                        <ArrowRight size={14} className="text-gray-400" />
                    </div>
                </div>
                <button className="w-full mt-2 py-1.5 text-xs font-bold text-health-600 dark:text-health-400 bg-health-50 dark:bg-health-900/20 rounded-lg">
                    View Map
                </button>
            </div>
        );
    }
    if (widget.type === 'swapMeal') {
        return (
            <div className="mt-2 p-3 bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Suggestion</p>
                <div className="flex gap-3">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center text-xl">🥗</div>
                    <div>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">Quinoa Power Bowl</p>
                        <p className="text-xs text-gray-500">350 kcal • 15min prep</p>
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <button className="flex-1 py-1.5 bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-gray-300 rounded-lg text-xs font-bold">Details</button>
                    <button className="flex-1 py-1.5 bg-health-600 text-white rounded-lg text-xs font-bold">Select</button>
                </div>
            </div>
        );
    }
    return null;
  };

  return (
    <div className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-50 flex flex-col items-end">

      {isOpen && (
        <div className="mb-4 w-[350px] md:w-[400px] h-[550px] glass-panel bg-white/95 dark:bg-stone-900/95 rounded-2xl shadow-2xl border border-white dark:border-stone-700 flex flex-col overflow-hidden animate-fade-in-up backdrop-blur-xl">
          {/* Header */}
          <div className="p-4 border-b border-gray-100 dark:border-stone-800 flex justify-between items-center bg-gradient-to-r from-health-50 to-transparent dark:from-health-900/20">
             <div className="flex items-center gap-2">
                <div className="p-1.5 bg-health-600 rounded-lg text-white">
                    <Sparkles size={16} />
                </div>
                <div>
                    <h3 className="font-medium text-gray-800 dark:text-white text-sm">Wellness Assistant</h3>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Powered by Gemini 2.5</p>
                </div>
             </div>
             <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={18} />
             </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50/50 dark:bg-black/20" ref={scrollRef}>
             {messages.map((msg, idx) => (
                <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user' 
                        ? 'bg-gray-800 dark:bg-stone-700 text-white rounded-tr-none' 
                        : 'bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-700 text-gray-700 dark:text-gray-200 rounded-tl-none shadow-sm'
                    }`}>
                        {msg.text}
                    </div>
                    {msg.widget && renderWidget(msg.widget)}
                </div>
             ))}
             {loading && (
                 <div className="flex justify-start">
                    <div className="bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-700 p-4 rounded-2xl rounded-tl-none shadow-sm max-w-[80%] w-full">
                        <div className="space-y-2 animate-pulse">
                            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full w-3/4"></div>
                            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full w-1/2"></div>
                            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full w-5/6"></div>
                        </div>
                    </div>
                 </div>
             )}
          </div>

          {/* Input & Quick Actions */}
          <div className="p-3 bg-white dark:bg-stone-900 border-t border-gray-100 dark:border-stone-800">

             {/* Quick Actions Chips */}
             <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar pb-1">
                {quickActions.map((action, i) => (
                    <button
                        key={i}
                        onClick={() => handleSend(action.query)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 dark:bg-stone-800 hover:bg-health-50 dark:hover:bg-health-900/20 border border-transparent hover:border-health-200 dark:hover:border-health-800 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-health-700 dark:hover:text-health-400 transition-all whitespace-nowrap"
                    >
                        <action.icon size={12} /> {action.label}
                    </button>
                ))}
             </div>

             <div className="relative">
                <input
                    type="text"
                    className="w-full bg-gray-50 dark:bg-stone-800 border border-gray-200 dark:border-stone-700 rounded-full pl-4 pr-12 py-3 text-sm focus:outline-none focus:border-health-600 dark:focus:border-health-500 transition-colors text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="Ask about diet, BMI, or wellness..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                />
                <button
                    onClick={() => handleSend()}
                    className={`absolute right-1 top-1 p-2 rounded-full transition-colors ${input.trim() ? 'bg-health-600 text-white' : 'bg-gray-200 dark:bg-stone-700 text-gray-400'}`}
                >
                    <Send size={16} />
                </button>
             </div>
          </div>
        </div>
      )}

      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="h-14 w-14 rounded-full bg-black dark:bg-white text-white dark:text-black shadow-lg flex items-center justify-center hover:scale-105 transition-transform z-50 hover:shadow-xl"
      >
        {isOpen ? <X size={24} /> : <MessageSquare size={24} />}
      </button>
    </div>
  );
};

export default ChatWidget;